import React from 'react';
import { Box, Typography, Link } from '@mui/material';
import {
  mergeBrandingTitleSx,
  sxFromBrandingStyle,
  DEFAULT_PUBLIC_TAGLINE_STYLE
} from '../../utils/brandingStyles';

const DEFAULT_PUBLIC_TAGLINE =
  'ERP for Consulting — unify sales, delivery, HR, and compliance for professional services firms.';

function websiteHref(raw) {
  const t = String(raw || '').trim();
  if (!t) return '#';
  if (/^https?:\/\//i.test(t)) return t;
  return `https://${t}`;
}

function displayTitle(companyName, logoSrc, variant) {
  const c = companyName && String(companyName).trim();
  if (c) return c;
  if (logoSrc) return '';
  return variant === 'login' ? 'ERP Consulting' : '';
}

function resolveTagline(companyTagline) {
  const t = companyTagline && String(companyTagline).trim();
  return t || DEFAULT_PUBLIC_TAGLINE;
}

function resolvePublicTaglineStyle(companyTaglineStyle) {
  return {
    ...DEFAULT_PUBLIC_TAGLINE_STYLE,
    ...(companyTaglineStyle || {})
  };
}

function logoPlateSx(variant) {
  if (variant === 'toolbar') {
    return {
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      px: 1,
      py: 0.5,
      borderRadius: 1.5,
      bgcolor: 'rgba(255,255,255,0.96)',
      border: '1px solid',
      borderColor: 'rgba(15,23,42,0.08)',
      boxShadow: '0 1px 3px rgba(15,23,42,0.12)',
    };
  }
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    px: 2.5,
    py: 1.75,
    mb: 1.5,
    mx: 'auto',
    borderRadius: 2,
    bgcolor: 'rgba(255,255,255,0.98)',
    border: '1px solid',
    borderColor: 'rgba(15,23,42,0.1)',
    boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
  };
}

/**
 * Shared public branding: logo + company block + tagline.
 * @param {'toolbar'|'login'} variant
 * @param {'light'|'dark'} [toolbarColorScheme]
 */
export function PublicBrandingIdentity({
  logoSrc,
  companyName,
  companyAddress,
  companyCity,
  companyZip,
  companyWebsite,
  companyContactEmail,
  companyTagline,
  companyNameStyle,
  companyTaglineStyle,
  variant = 'toolbar',
  loginTextPrimary,
  loginTextSecondary,
  toolbarColorScheme = 'light'
}) {
  const title = displayTitle(companyName, logoSrc, variant);
  const showGradientFallback = variant === 'login' && !logoSrc && !(companyName && String(companyName).trim());
  const cityLine = [companyCity, companyZip].filter((x) => x && String(x).trim()).join(', ');
  const addressLineToolbar = [companyAddress, cityLine].filter((x) => x && String(x).trim()).join(' · ');
  const hasContactLinks = Boolean(
    (companyWebsite && String(companyWebsite).trim()) || (companyContactEmail && String(companyContactEmail).trim())
  );
  const publicTagStyle = resolvePublicTaglineStyle(companyTaglineStyle);

  const titleSxBase =
    variant === 'toolbar'
      ? {
          fontFamily: '"Georgia", "Cambria", "Times New Roman", serif',
          fontWeight: 700,
          fontSize: '1.05rem',
          lineHeight: 1.25,
          letterSpacing: '0.03em',
          color: toolbarColorScheme === 'dark' ? 'common.white' : 'text.primary',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical'
        }
      : {
          fontFamily: '"Georgia", "Cambria", "Times New Roman", serif',
          fontWeight: 700,
          fontSize: '1.65rem',
          lineHeight: 1.2,
          letterSpacing: '0.04em',
          color: loginTextPrimary || 'inherit',
          mb: 0.5
        };

  const titleSx = mergeBrandingTitleSx(titleSxBase, companyNameStyle);

  const metaColor =
    variant === 'toolbar'
      ? toolbarColorScheme === 'dark'
        ? 'grey.300'
        : 'text.secondary'
      : loginTextSecondary || 'text.secondary';

  const metaRow = (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 0.5,
        justifyContent: variant === 'login' ? 'center' : 'flex-start'
      }}
    >
      {companyWebsite && String(companyWebsite).trim() ? (
        <Link
          href={websiteHref(companyWebsite)}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          variant="caption"
          color="inherit"
          sx={{ fontWeight: 500 }}
        >
          {String(companyWebsite).trim()}
        </Link>
      ) : null}
      {companyWebsite && companyContactEmail ? (
        <Typography component="span" variant="caption" sx={{ color: metaColor, opacity: 0.7 }}>
          ·
        </Typography>
      ) : null}
      {companyContactEmail && String(companyContactEmail).trim() ? (
        <Link
          href={`mailto:${String(companyContactEmail).trim()}`}
          underline="hover"
          variant="caption"
          color="inherit"
          sx={{ fontWeight: 500 }}
        >
          {String(companyContactEmail).trim()}
        </Link>
      ) : null}
    </Box>
  );

  const taglineBaseToolbar = {
    display: 'block',
    mt: 0.5,
    fontStyle: publicTagStyle.fontStyle === 'normal' ? 'normal' : 'italic',
    fontFamily: publicTagStyle.fontFamily || '"Georgia", "Cambria", serif',
    fontWeight: publicTagStyle.fontWeight || 600,
    fontSize: publicTagStyle.fontSize || '0.95rem',
    letterSpacing: publicTagStyle.letterSpacing || '0.03em',
    color:
      publicTagStyle.color ||
      (toolbarColorScheme === 'dark' ? 'rgba(255,255,255,0.95)' : '#1e3a8a'),
    lineHeight: 1.35,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  };

  if (variant === 'toolbar') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          minWidth: 0,
          flex: 1
        }}
      >
        {logoSrc ? (
          <Box sx={logoPlateSx('toolbar')}>
            <Box
              component="img"
              src={logoSrc}
              alt=""
              sx={{
                height: 40,
                maxWidth: 150,
                width: 'auto',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </Box>
        ) : null}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {title ? (
            <Typography component="div" sx={titleSx}>
              {title}
            </Typography>
          ) : null}
          {addressLineToolbar ? (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                lineHeight: 1.35,
                mt: 0.25,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: metaColor
              }}
            >
              {addressLineToolbar}
            </Typography>
          ) : null}
          {hasContactLinks ? (
            <Box sx={{ mt: addressLineToolbar ? 0.15 : 0.25, lineHeight: 1.2 }}>{metaRow}</Box>
          ) : null}
          <Typography
            component="div"
            sx={mergeBrandingTitleSx(taglineBaseToolbar, companyTaglineStyle)}
          >
            {resolveTagline(companyTagline)}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ textAlign: 'center', mb: 0 }}>
      {logoSrc ? (
        <Box sx={logoPlateSx('login')}>
          <Box
            component="img"
            src={logoSrc}
            alt=""
            sx={{
              maxHeight: 64,
              maxWidth: '100%',
              width: 'auto',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </Box>
      ) : null}
      {showGradientFallback ? (
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 0.5
          }}
        >
          {title}
        </Typography>
      ) : title ? (
        <Typography component="div" sx={titleSx}>
          {title}
        </Typography>
      ) : null}
      {companyAddress && String(companyAddress).trim() ? (
        <Typography variant="body2" sx={{ color: metaColor, mt: 0.75, lineHeight: 1.5 }}>
          {String(companyAddress).trim()}
        </Typography>
      ) : null}
      {cityLine ? (
        <Typography variant="body2" sx={{ color: metaColor, mt: 0.25, lineHeight: 1.5 }}>
          {cityLine}
        </Typography>
      ) : null}
      {hasContactLinks ? (
        <Box sx={{ mt: companyAddress || cityLine ? 1 : 0.75, display: 'flex', justifyContent: 'center' }}>
          {metaRow}
        </Box>
      ) : null}
      <Typography
        component="div"
        sx={(() => {
          const merged = resolvePublicTaglineStyle(companyTaglineStyle);
          const tagExtra = sxFromBrandingStyle(merged);
          return {
            mt: 1.75,
            fontStyle: merged.fontStyle === 'normal' ? 'normal' : 'italic',
            fontFamily: merged.fontFamily || '"Georgia", "Cambria", "Times New Roman", serif',
            fontWeight: merged.fontWeight || 600,
            fontSize: merged.fontSize || '1.2rem',
            letterSpacing: merged.letterSpacing || '0.04em',
            lineHeight: 1.55,
            maxWidth: 520,
            mx: 'auto',
            color: merged.color || loginTextPrimary || '#1e3a8a',
            textShadow: '0 1px 0 rgba(255,255,255,0.35)',
            ...tagExtra
          };
        })()}
      >
        {resolveTagline(companyTagline)}
      </Typography>
    </Box>
  );
}
